import React, { useMemo, useState } from 'react';
import type { CashflowPayload, Filters, FoundationPayload, Company } from '../types/biApp';
import { formatMoney, formatMoneyAxis, formatNumber, formatDate } from '../shared/formatting';
import { BalanceCard, LineChart, MiniChart, WaterfallChart, type WaterfallBar } from '../shared/charts';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { CashflowScheduleTab, CashflowForwardForecastTab, UraScheduleTab } from './cashflowSchedule';
import { ArAgingTab } from './arAgingTab';
import { Crumbs, Tabs, Card as UiCard, Btn, Pill, DataTable, ui, type Col } from '../shared/ui';
import { readUrlParam, useUrlSync } from '../shared/urlState';

const isHrms = () => typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

type CashflowView = 'cockpit' | 'daily' | 'waterfall' | 'forecast' | 'schedule' | 'forward' | 'ura' | 'ar_aging' | 'correct';

type CorrectionAccount = { id: number; company_code: string; iban: string; bank_name: string | null; label: string | null; balance_eur: number; balance_date: string | null; source: string | null; excluded: boolean };

/** Superadmin panel — ispravak stanja bankovnog računa (novi anchor) + izuzimanje iz CF-a (oročenje). */
export function BalanceCorrectionPanel({ token, onSaved }: { token: string; onSaved?: () => void }) {
    const [accounts, setAccounts] = useState<CorrectionAccount[] | null>(null);
    const [gated, setGated] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState<{ balance: string; date: string; exclude: boolean }>({ balance: '', date: '', exclude: false });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [sortKey, setSortKey] = useState<'company_code' | 'balance_eur' | 'balance_date' | 'excluded' | 'iban'>('company_code');
    const [sortDir, setSortDir] = useState<1 | -1>(1);
    const sortBy = (k: typeof sortKey) => { if (sortKey === k) { setSortDir((d) => (d === 1 ? -1 : 1)); } else { setSortKey(k); setSortDir(1); } };

    const load = React.useCallback(() => {
        fetch('/api/cashflow/accounts', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (r.status === 403) { setGated(true); return null; } return r.json(); })
            .then((d) => { if (d) setAccounts(d.accounts ?? []); })
            .catch(() => setAccounts([]));
    }, [token]);
    React.useEffect(() => { load(); }, [load]);

    const startEdit = (a: CorrectionAccount) => {
        setEditId(a.id);
        const d = a.balance_date ? a.balance_date.slice(0, 10).split('-').reverse().join('.') + '.' : '';
        setForm({ balance: String(a.balance_eur), date: d, exclude: a.excluded });
        setMsg('');
    };
    const save = async (id: number) => {
        setBusy(true); setMsg('');
        try {
            const res = await fetch(`/api/cashflow/accounts/${id}/correct`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ balance_eur: Number(String(form.balance).replace(',', '.')), as_of_date: form.date, exclude_from_cashflow: form.exclude }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); setMsg('Greška: ' + (e.message ?? res.status)); setBusy(false); return; }
            setEditId(null); load(); onSaved?.();
        } catch { setMsg('Greška u spremanju.'); }
        setBusy(false);
    };

    const D = { bg: '#1c1e22', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227', green: '#3fae6b', red: '#c05a5a' };
    if (gated) return <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 10, padding: 14, color: D.dim }}>Ispravak stanja je dostupan samo superadministratoru.</div>;
    if (accounts === null) return <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 10, padding: 14, color: D.dim }}>Učitavam račune…</div>;
    const iCell: React.CSSProperties = { padding: '6px 8px' };
    const sorted = [...accounts].sort((a, b) => {
        const av = a[sortKey] as string | number | boolean | null;
        const bv = b[sortKey] as string | number | boolean | null;
        if (typeof av === 'string' || typeof bv === 'string') return sortDir * String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir * ((Number(av ?? 0)) - (Number(bv ?? 0)));
    });
    const arrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : '';
    const hCell: React.CSSProperties = { ...iCell, cursor: 'pointer', userSelect: 'none' };

    return (
        <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 12, padding: 16, color: D.ink }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>🔧 Ispravak stanja bankovnog računa</h3>
            <p style={{ color: D.dim, fontSize: 12.5, margin: '0 0 12px' }}>Postavi točno stanje na određeni datum (novi anchor); CF od tog datuma gradi po izvodima. Izuzmi račun (npr. prekonoćno oročenje) da se ne broji u raspoloživi novac. Klik na naslov = sortiraj.</p>
            {msg ? <p style={{ color: D.red, fontWeight: 600 }}>{msg}</p> : null}
            <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ textAlign: 'left', color: D.dim, fontSize: 11, position: 'sticky', top: 0, background: D.panel }}>
                        <th style={hCell} onClick={() => sortBy('company_code')}>Firma{arrow('company_code')}</th>
                        <th style={hCell} onClick={() => sortBy('iban')}>IBAN · banka{arrow('iban')}</th>
                        <th style={{ ...hCell, textAlign: 'right' }} onClick={() => sortBy('balance_eur')}>Stanje (€){arrow('balance_eur')}</th>
                        <th style={hCell} onClick={() => sortBy('balance_date')}>Na dan{arrow('balance_date')}</th>
                        <th style={hCell} onClick={() => sortBy('excluded')}>U CF-u{arrow('excluded')}</th>
                        <th style={iCell}></th>
                    </tr></thead>
                    <tbody>
                        {sorted.map((a) => editId === a.id ? (
                            <tr key={a.id} style={{ background: 'rgba(201,162,39,0.12)', borderTop: `1px solid ${D.line}` }}>
                                <td style={iCell}>{a.company_code}</td>
                                <td style={{ ...iCell, fontFamily: 'monospace', fontSize: 11, color: D.dim }}>{a.iban}</td>
                                <td style={iCell}><input type="text" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} style={{ width: 120, textAlign: 'right', background: D.bg, color: D.ink, border: `1px solid ${D.line}`, borderRadius: 5, padding: '3px 6px' }} /></td>
                                <td style={iCell}><input type="text" placeholder="dd.mm.gggg" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={{ width: 100, background: D.bg, color: D.ink, border: `1px solid ${D.line}`, borderRadius: 5, padding: '3px 6px' }} /></td>
                                <td style={iCell}><label style={{ fontSize: 12, color: D.dim }}><input type="checkbox" checked={!form.exclude} onChange={(e) => setForm({ ...form, exclude: !e.target.checked })} /> broji</label></td>
                                <td style={{ ...iCell, whiteSpace: 'nowrap' }}>
                                    <button type="button" disabled={busy} onClick={() => save(a.id)} style={{ padding: '3px 8px', background: D.green, color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>Spremi</button>{' '}
                                    <button type="button" disabled={busy} onClick={() => setEditId(null)} style={{ padding: '3px 8px', background: 'transparent', color: D.dim, border: `1px solid ${D.line}`, borderRadius: 5, cursor: 'pointer' }}>Odustani</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={a.id} style={{ opacity: a.excluded ? 0.5 : 1, borderTop: `1px solid ${D.line}` }}>
                                <td style={iCell}>{a.company_code}</td>
                                <td style={{ ...iCell, fontFamily: 'monospace', fontSize: 11, color: D.dim }}>…{a.iban.slice(-8)} <span>{a.bank_name ?? ''}</span></td>
                                <td style={{ ...iCell, textAlign: 'right', fontWeight: 600 }}>{formatMoney(a.balance_eur)}</td>
                                <td style={{ ...iCell, color: D.dim }}>{a.balance_date ? a.balance_date.slice(0, 10).split('-').reverse().join('.') + '.' : '—'}</td>
                                <td style={iCell}>{a.excluded ? <span style={{ color: D.red }}>izuzet</span> : <span style={{ color: D.green }}>✓</span>}</td>
                                <td style={iCell}><button type="button" onClick={() => startEdit(a)} style={{ padding: '3px 10px', background: D.gold, color: '#1c1e22', border: 0, borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>Ispravi</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function DrilldownEntries({ payload, filters, onChange, locale = 'hr' }: { payload: CashflowPayload; filters: Filters; onChange: (patch: Partial<Filters>) => void; locale?: 'hr' | 'en' }) {
    const totalPages = Math.max(1, Math.ceil(payload.entries.total / payload.entries.limit));
    const selectedAccount = payload.accounts.find((account) => String(account.id) === filters.accountId);
    const [query, setQuery] = useState('');
    const filteredRows = payload.entries.rows.filter((entry) => matchesQuickSearch([
        entry.company_code,
        entry.iban,
        entry.statement_number,
        entry.statement_date,
        entry.partner_name,
        entry.description,
        entry.payment_reference,
        entry.debit,
        entry.credit,
        entry.net_amount,
        formatMoney(entry.debit),
        formatMoney(entry.credit),
        formatMoney(entry.net_amount),
        entry.related_party_name,
        entry.is_internal_transfer ? 'interni transfer' : '',
        entry.is_potential_loan ? 'potencijalna pozajmica' : '',
    ], query));

    const meta = (
        <>
            {selectedAccount ? `${selectedAccount.company_code} · ${selectedAccount.iban} · ` : ''}
            {formatDate(filters.dateFrom, locale)} - {formatDate(filters.dateTo, locale)} · {payload.entries.total} ukupno, stranica {filters.page} od {totalPages}; prikazano {filteredRows.length}
        </>
    );

    if (isHrms()) {
        const cols: Col<typeof filteredRows[number]>[] = [
            { key: 'statement_date', label: 'Datum', render: (entry) => formatDate(entry.statement_date, locale) },
            { key: 'company_code', label: 'Firma', render: (entry) => entry.company_code },
            { key: 'partner', label: 'Partner / opis', render: (entry) => <div style={{ minWidth: 0 }}><div style={{ fontWeight: 650, color: ui.ink }}>{entry.partner_name ?? 'Bez partnera'}</div><div style={{ fontSize: '0.74rem', color: ui.muted }}>{entry.description}</div></div> },
            { key: 'debit', label: 'Duguje', align: 'right', render: (entry) => formatMoney(entry.debit) },
            { key: 'credit', label: 'Potražuje', align: 'right', render: (entry) => formatMoney(entry.credit) },
            { key: 'net_amount', label: 'Neto', align: 'right', render: (entry) => formatMoney(entry.net_amount) },
            { key: 'tag', label: 'Tag', render: (entry) => <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>{entry.is_potential_loan ? <Pill tone="warn">pozajmica</Pill> : null}{entry.is_internal_transfer ? <Pill tone="info">interno</Pill> : null}</span> },
        ];
        return (
            <UiCard pad={18}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>Drilldown stavki izvoda</div>
                        <div style={{ fontSize: '0.8rem', color: ui.muted, marginTop: 4 }}>{meta}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <QuickSearchInput value={query} onChange={setQuery} placeholder="Traži datum, partnera, opis ili iznos…" />
                        <Btn size="sm" variant="soft" onClick={() => onChange({ accountId: '' })}>Natrag na sve račune</Btn>
                        <Btn size="sm" disabled={filters.page <= 1} onClick={() => onChange({ page: filters.page - 1 })}>Prethodna</Btn>
                        <Btn size="sm" disabled={filters.page >= totalPages} onClick={() => onChange({ page: filters.page + 1 })}>Sljedeća</Btn>
                    </div>
                </div>
                <UiCard pad={0}>
                    <DataTable cols={cols} rows={filteredRows} empty="Nema stavki." />
                </UiCard>
            </UiCard>
        );
    }

    return (
        <section className="card nested-card">
            <div className="module-heading">
                <div>
                    <h3>Drilldown stavki izvoda</h3>
                    <p className="muted">
                        {selectedAccount ? `${selectedAccount.company_code} · ${selectedAccount.iban} · ` : ''}
                        {formatDate(filters.dateFrom, locale)} - {formatDate(filters.dateTo, locale)} · {payload.entries.total} ukupno, stranica {filters.page} od {totalPages}; prikazano {filteredRows.length}
                    </p>
                </div>
                <div className="pager">
                    <QuickSearchInput value={query} onChange={setQuery} placeholder="Traži datum, partnera, opis ili iznos…" />
                    <button className="secondary compact" type="button" onClick={() => onChange({ accountId: '' })}>Natrag na sve račune</button>
                    <button className="secondary compact" disabled={filters.page <= 1} type="button" onClick={() => onChange({ page: filters.page - 1 })}>Prethodna</button>
                    <button className="secondary compact" disabled={filters.page >= totalPages} type="button" onClick={() => onChange({ page: filters.page + 1 })}>Sljedeća</button>
                </div>
            </div>
            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Datum</th>
                            <th>Firma</th>
                            <th>Partner / opis</th>
                            <th>Duguje</th>
                            <th>Potražuje</th>
                            <th>Neto</th>
                            <th>Tag</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.map((entry) => (
                            <tr key={entry.id}>
                                <td>{formatDate(entry.statement_date, locale)}</td>
                                <td>{entry.company_code}</td>
                                <td><strong>{entry.partner_name ?? 'Bez partnera'}</strong><p className="muted">{entry.description}</p></td>
                                <td>{formatMoney(entry.debit)}</td>
                                <td>{formatMoney(entry.credit)}</td>
                                <td>{formatMoney(entry.net_amount)}</td>
                                <td>
                                    {entry.is_potential_loan ? <span className="pill">pozajmica</span> : null}
                                    {entry.is_internal_transfer ? <span className="pill">interno</span> : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


// ── Treasury Cockpit (tamna varijanta) ─────────────────────────────────────
const fmtC = (n: number): string => {
    const a = Math.abs(n);
    if (a >= 1e6) return (n / 1e6).toFixed(2).replace('.', ',') + 'M €';
    if (a >= 1e4) return Math.round(n / 1e3) + 'k €';
    return new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n) + ' €';
};
const COCK = { bg: '#1c1e22', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227', green: '#3fae6b', red: '#c05a5a', amber: '#d8a24a' };

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'green' | 'red' | 'gold' | 'amber' }) {
    const c = tone === 'green' ? COCK.green : tone === 'red' ? COCK.red : tone === 'gold' ? COCK.gold : tone === 'amber' ? COCK.amber : COCK.ink;
    return (
        <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: '9px 12px', minWidth: 130, flex: 1 }}>
            <div style={{ fontSize: 11, color: COCK.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c, lineHeight: 1.1 }}>{value}</div>
            {sub ? <div style={{ fontSize: 11, color: COCK.dim, marginTop: 2 }}>{sub}</div> : null}
        </div>
    );
}

function Sparkline({ pts, color }: { pts: number[]; color: string }) {
    if (pts.length < 2) return <span style={{ color: COCK.dim, fontSize: 10 }}>—</span>;
    const min = Math.min(...pts), max = Math.max(...pts), rng = max - min || 1;
    const w = 60, h = 18;
    const d = pts.map((v, i) => `${(i / (pts.length - 1)) * w},${h - ((v - min) / rng) * h}`).join(' ');
    return <svg width={w} height={h}><polyline points={d} fill="none" stroke={color} strokeWidth={1.4} /></svg>;
}

// Raspon projekcije: dani unaprijed; 13 tj. se agregira na kraj tjedna. Zadano 7 d.
const PROJ_RANGES = [
    { key: '7d', days: 7, label: '7 dana', short: '7 d' },
    { key: '15d', days: 15, label: '15 dana', short: '15 d' },
    { key: '30d', days: 30, label: '30 dana', short: '30 d' },
    { key: '13w', days: 91, label: '13 tjedana', short: '13 tj.' },
] as const;
type ProjRangeKey = (typeof PROJ_RANGES)[number]['key'];
const PAST_DAYS = 4;

const isoAdd = (iso: string, n: number): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};
const dayMonth = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
// Os Y u tisucama EUR, ista jedinica kao lista Nadolazeca placanja.
const fmtK = (v: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(Math.round(v / 1e3)) + 'k';
const niceStep = (raw: number) => {
    const p = Math.pow(10, Math.floor(Math.log10(raw)));
    return ([1, 2, 2.5, 5, 10].find((m) => m * p >= raw) ?? 10) * p;
};

function ProjectionChart({ payload, range }: { payload: CashflowPayload; range: ProjRangeKey }) {
    const cfg = PROJ_RANGES.find((r) => r.key === range) ?? PROJ_RANGES[0];
    const start = payload.treasury?.available_today ?? payload.balances.consolidated.closing;
    const asOf = (payload.treasury?.as_of ?? new Date().toISOString()).slice(0, 10);
    const end = isoAdd(asOf, cfg.days);

    // Projekcija po danu; dospjelo prije as_of ulazi u prvi dan projekcije.
    const flow: Record<string, number> = {};
    payload.projection.items.forEach((it) => {
        const d = it.date <= asOf ? isoAdd(asOf, 1) : it.date;
        flow[d] = (flow[d] ?? 0) + (it.direction === 'inflow' ? it.amount : -it.amount);
    });
    const daily: Record<string, number> = { [asOf]: start };
    let run = start;
    for (let i = 1; i <= cfg.days; i++) { const d = isoAdd(asOf, i); run += flow[d] ?? 0; daily[d] = run; }

    // Stvarno stanje 4 prethodna dana: od stanja na as_of unatrag oduzima se promet s izvoda.
    const net: Record<string, number> = {};
    payload.account_history.forEach((h) => h.points.forEach((p) => { const d = p.date.slice(0, 10); net[d] = (net[d] ?? 0) + p.net; }));
    const past: Array<{ date: string; val: number }> = [];
    let back = start;
    for (let k = 1; k <= PAST_DAYS; k++) { back -= net[isoAdd(asOf, -(k - 1))] ?? 0; past.unshift({ date: isoAdd(asOf, -k), val: back }); }

    const future = cfg.key === '13w'
        ? Array.from({ length: 13 }, (_, w) => { const d = isoAdd(asOf, 7 * (w + 1)); return { date: d, val: daily[d] }; })
        : Array.from({ length: cfg.days }, (_, i) => { const d = isoAdd(asOf, i + 1); return { date: d, val: daily[d] }; });
    const actual = [...past, { date: asOf, val: start }];
    const forecast = [{ date: asOf, val: start }, ...future];

    const vals = [...actual, ...future].map((p) => p.val);
    const step = niceStep(((Math.max(...vals, 0) - Math.min(...vals, 0)) || 1) / 5);
    const yMin = Math.floor(Math.min(...vals, 0) / step) * step;
    const yMax = Math.ceil(Math.max(...vals, 0) / step) * step || step;
    const ticks: number[] = [];
    for (let t = yMin; t <= yMax + step / 2; t += step) ticks.push(t);

    const W = 560, H = 230, padL = 50, padR = 10, padT = 14, padB = 24;
    const first = past[0].date;
    const span = (Date.parse(end) - Date.parse(first)) / 864e5 || 1;
    const x = (iso: string) => padL + ((Date.parse(iso) - Date.parse(first)) / 864e5 / span) * (W - padL - padR);
    const y = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * (H - padT - padB);
    const line = (pts: Array<{ date: string; val: number }>) => pts.map((p) => `${x(p.date)},${y(p.val)}`).join(' ');
    const base = y(Math.max(yMin, Math.min(0, yMax)));
    const areaPath = `M${x(asOf)},${base} L${forecast.map((p) => `${x(p.date)},${y(p.val)}`).join(' L')} L${x(end)},${base} Z`;

    // Oznake osi X: datumska traka, najvise ~9 natpisa.
    // Za 13 tj. su prethodni dani preuski za natpis i stavke ne leze na tjednoj liniji.
    const weekly = cfg.key === '13w';
    const xDates = [...(weekly ? [] : past.map((p) => p.date)), asOf, ...future.map((p) => p.date)];
    const every = weekly ? 1 : Math.max(1, Math.ceil(xDates.length / 9));
    const xLabels = xDates.filter((d, i) => d === asOf || (i - (weekly ? 0 : PAST_DAYS)) % every === 0);

    const events = weekly ? [] : payload.projection.items
        .filter((e) => e.date > asOf && e.date <= end)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 4);
    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ overflow: 'visible' }}>
            <defs><linearGradient id="cockArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COCK.gold} stopOpacity="0.28" /><stop offset="100%" stopColor={COCK.gold} stopOpacity="0.02" /></linearGradient></defs>
            {ticks.map((t) => (
                <g key={t}>
                    <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke={COCK.line} strokeWidth={t === 0 ? 1.2 : 0.6} />
                    <text x={padL - 6} y={y(t) + 3} fontSize={9} fill={COCK.dim} textAnchor="end">{fmtK(t)}</text>
                </g>
            ))}
            <text x={padL - 6} y={padT - 5} fontSize={9} fill={COCK.dim} textAnchor="end">tis. €</text>
            {xLabels.map((d) => (
                <g key={d}>
                    <line x1={x(d)} x2={x(d)} y1={H - padB} y2={H - padB + 3} stroke={COCK.dim} strokeWidth={0.6} />
                    <text x={x(d)} y={H - padB + 13} fontSize={9} fill={d === asOf ? COCK.ink : COCK.dim} textAnchor="middle">{dayMonth(d)}</text>
                </g>
            ))}
            <line x1={x(asOf)} x2={x(asOf)} y1={padT} y2={H - padB} stroke={COCK.dim} strokeWidth={0.8} strokeDasharray="2 3" />
            <text x={x(asOf) + 3} y={padT + 8} fontSize={9} fill={COCK.dim}>stanje {dayMonth(asOf)}</text>
            <path d={areaPath} fill="url(#cockArea)" />
            <polyline points={line(actual)} fill="none" stroke={COCK.gold} strokeWidth={2} />
            <polyline points={line(forecast)} fill="none" stroke={COCK.gold} strokeWidth={2} strokeDasharray="5 4" />
            {actual.map((p) => <circle key={p.date} cx={x(p.date)} cy={y(p.val)} r={2} fill={COCK.gold}><title>{`${dayMonth(p.date)} ${fmtC(p.val)}`}</title></circle>)}
            {future.map((p) => <circle key={p.date} cx={x(p.date)} cy={y(p.val)} r={1.6} fill={COCK.gold}><title>{`${dayMonth(p.date)} ${fmtC(p.val)}`}</title></circle>)}
            {events.map((e, k) => {
                const inflow = e.direction === 'inflow';
                const v = daily[e.date];
                return (
                    <g key={k}>
                        <circle cx={x(e.date)} cy={y(v)} r={3.5} fill={inflow ? COCK.green : COCK.red} />
                        <text x={x(e.date)} y={y(v) - 8} fontSize={9} fill={inflow ? COCK.green : COCK.red} textAnchor="middle">{(inflow ? '+' : '−') + fmtC(e.amount)}</text>
                    </g>
                );
            })}
        </svg>
    );
}

function MiniWaterfall({ payload }: { payload: CashflowPayload }) {
    const b = payload.balances.consolidated;
    const realCredit = payload.consolidated.credit;
    const realDebit = payload.consolidated.debit;
    const xfer = payload.transfers;
    const bars = [
        { label: 'Otvoreno', v: b.opening, cum: b.opening },
        { label: 'Priljev', v: Math.max(0, realCredit), delta: true },
        { label: 'Odljev', v: -Math.abs(realDebit), delta: true },
        { label: 'Zatvoreno', v: b.closing, cum: b.closing },
    ];
    const all = [b.opening, b.closing, Math.abs(realCredit), Math.abs(realDebit)];
    const max = Math.max(...all, 1);
    const H = 90;
    let cum = b.opening;
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: H + 24 }}>
            {bars.map((bar, i) => {
                let top: number, hgt: number, col: string;
                if (i === 0 || i === 3) { hgt = (Math.abs(bar.v) / max) * H; top = H - hgt; col = COCK.gold; }
                else { const prev = cum; cum += bar.v; const hi = Math.max(prev, cum), lo = Math.min(prev, cum); hgt = ((hi - lo) / max) * H; top = H - (hi / max) * H; col = bar.v >= 0 ? COCK.green : COCK.red; }
                return (
                    <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ height: H, position: 'relative' }}><div style={{ position: 'absolute', left: '15%', right: '15%', top, height: Math.max(2, hgt), background: col, borderRadius: 3 }} /></div>
                        <div style={{ fontSize: 10, color: COCK.dim, marginTop: 4 }}>{bar.label}</div>
                        <div style={{ fontSize: 10, color: COCK.ink }}>{fmtC(i === 1 ? Math.abs(realCredit) : i === 2 ? Math.abs(realDebit) : bar.v)}</div>
                    </div>
                );
            })}
        </div>
    );
}

function TransferFootnote({ payload }: { payload: CashflowPayload }) {
    const t = payload.transfers;
    if (!t || (t.credit <= 0 && t.debit <= 0)) return null;
    return (
        <div style={{ fontSize: 10, color: COCK.dim, marginTop: 6, lineHeight: 1.45 }}>
            Prijenosi žr↔žr (isključeni iz grafa): priliv {fmtC(t.credit)} · odliv {fmtC(t.debit)}
        </div>
    );
}

export function CashflowCockpit({ payload, filterBar }: { payload: CashflowPayload; filterBar?: React.ReactNode }) {
    const tr = payload.treasury;
    const histByAcc: Record<number, number[]> = {};
    payload.account_history.forEach((h) => { histByAcc[h.account_id] = h.points.map((p) => p.running_balance); });
    // samo računi s feedom I stanjem različitim od 0 (prazni podračuni se ne prikazuju); ručni unos (SLOVENIJA,
    // BUGARSKA) uvijek, i s nulom, da se vidi da ga treba upisati u Ispravak.
    const feedAccounts = payload.balances.accounts.filter((a) => a.is_manual || Math.round((a.closing ?? 0) as number) !== 0);
    // Grupa po tvrtki, redoslijed tvrtki kako dolaze; zbrojni red po tvrtki.
    const accGroups: Array<{ code: string; rows: typeof feedAccounts; total: number }> = [];
    feedAccounts.forEach((a) => {
        let g = accGroups.find((x) => x.code === a.company_code);
        if (!g) { g = { code: a.company_code, rows: [], total: 0 }; accGroups.push(g); }
        g.rows.push(a); g.total += (a.closing ?? 0) as number;
    });
    const fmtTs = (ts?: string | null) => {
        const m = ts ? /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(ts) : null;
        return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : null;
    };
    const fmtD = (d?: string | null) => { const m = d ? /^(\d{4})-(\d{2})-(\d{2})/.exec(d) : null; return m ? `${m[3]}.${m[2]}.${m[1]}.` : null; };
    const accStamp = (a: (typeof feedAccounts)[number]) => {
        if (a.is_manual) {
            const at = fmtTs(a.last_correction_at);
            return at ? `ručni unos ${at}${a.anchor_date ? ` · stanje ${fmtD(a.anchor_date)}` : ''}` : 'ručni unos · još nije upisano (Ispravak)';
        }
        const imp = fmtTs(a.last_import_at);
        const d = fmtD(a.last_statement_date);
        return imp ? `izvod ${d ?? '—'} · unesen ${imp}` : 'nema izvoda';
    };
    const fmt0 = (v: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(v);
    const fmtDay = (d: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d); return m ? `${m[3]}.${m[2]}` : d; };
    // Agregacija nadolazećih po (dan, tip) — bez pojedinačnih imena klijenata.
    const SRC_LABEL: Record<string, string> = { gold_client_order: 'Naplata isporuka', tca_scrap: 'TCA izvoz loma', gold_supplier: 'Nabava zlata (dobavljači)', forecast_buyback: 'Otkup loma (procj.)', forecast_investment: 'Otkup investicijskog (procj.)', forecast_sales_investment: 'Prodaja investicijskog (procj.)', forecast_sales_trend: 'Forecast prodaje (modul 1)', sales_plan: 'Plan prodaje', ura_obligation: 'URA računi', payment_batch: 'Nalozi za plaćanje', payment_realized: 'Realizirano (čeka izvod)', facility_leasing: 'Leasing rate', facility_bank_credit: 'Krediti (rate)', facility_loan_book: 'Zajmovi (kamate)', manual: 'Ručni unos', contract: 'Ugovori (obroci)', expected_inflow: 'Priljevi (ručni unos)' };
    const upcomingAgg = (() => {
        const g: Record<string, { date: string; src: string; direction: string; amount: number; count: number }> = {};
        payload.projection.items.forEach((it) => {
            const st = it.source_type ?? 'other';
            const key = `${it.date}|${st}|${it.direction}`;
            if (!g[key]) g[key] = { date: it.date, src: st, direction: it.direction, amount: 0, count: 0 };
            g[key].amount += it.amount; g[key].count++;
        });
        return Object.values(g).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 18);
    })();

    // Font/zoom po segmentu — pamti se u localStorage (per browser/user).
    const [zoom, setZoom] = useState<Record<string, number>>(() => { try { return JSON.parse(localStorage.getItem('az_treasury_zoom') || '{}'); } catch { return {}; } });
    const z = (k: string) => zoom[k] ?? 1;
    const [projRange, setProjRange] = useState<ProjRangeKey>('7d');
    const projRangeCfg = PROJ_RANGES.find((r) => r.key === projRange) ?? PROJ_RANGES[0];
    const bump = (k: string, d: number) => setZoom((p) => { const n = { ...p, [k]: Math.max(0.7, Math.min(1.8, Math.round(((p[k] ?? 1) + d) * 100) / 100)) }; try { localStorage.setItem('az_treasury_zoom', JSON.stringify(n)); } catch { /* ignore */ } return n; });
    const ZC = (k: string) => (
        <span style={{ display: 'inline-flex', gap: 2, marginLeft: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => bump(k, -0.1)} title="manji font" style={{ width: 18, height: 18, lineHeight: '16px', textAlign: 'center', background: 'transparent', color: COCK.dim, border: `1px solid ${COCK.line}`, borderRadius: 4, cursor: 'pointer', fontSize: 12, padding: 0 }}>−</button>
            <button type="button" onClick={() => bump(k, 0.1)} title="veći font" style={{ width: 18, height: 18, lineHeight: '16px', textAlign: 'center', background: 'transparent', color: COCK.dim, border: `1px solid ${COCK.line}`, borderRadius: 4, cursor: 'pointer', fontSize: 12, padding: 0 }}>+</button>
        </span>
    );
    return (
        <div style={{ background: COCK.bg, color: COCK.ink, borderRadius: 14, padding: 16, marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>Cash-Flow <span style={{ color: COCK.dim, fontWeight: 400 }}>— konsolidirano</span></h2>
                <div style={{ marginLeft: 'auto' }}>{filterBar}</div>
            </div>
            {/* KPI strip */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>{ZC('kpi')}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', zoom: z('kpi') }}>
                <KpiCard label="Raspoloživo danas" value={fmtC(tr?.available_today ?? payload.balances.consolidated.closing)} tone="gold" />
                <KpiCard label="Projekcija 30 dana" value={(tr && tr.projection_30d.net >= 0 ? '+' : '') + fmtC(tr?.projection_30d.net ?? 0)} tone={tr && tr.projection_30d.net >= 0 ? 'green' : 'red'} />
                <KpiCard label="Cash runway" value={tr?.cash_runway_days != null ? `${tr.cash_runway_days} dana` : '∞'} />
                <KpiCard label="Dospijeće 7 dana" value={fmtC(tr?.due_7d ?? 0)} tone="amber" />
                <KpiCard label="Otvorene obveze" value={fmtC(tr?.open_obligations ?? 0)} />
            </div>
            {/* main grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1fr) minmax(320px,2.2fr) minmax(180px,1fr)', gap: 12 }}>
                {/* left: accounts */}
                <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: 12, zoom: z('accounts') }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: COCK.dim, marginBottom: 2 }}>Stanje po računu {ZC('accounts')}</div>
                    <div style={{ fontSize: 10, color: COCK.dim, marginBottom: 8 }}>stanje na računu u trenutku unosa izvoda (vrijeme uz svaki račun) · PBZ uključuje prekonoćno oročenje</div>
                    {accGroups.map((g) => (
                        <div key={g.code} style={{ marginBottom: 6 }}>
                            {g.rows.map((a) => (
                                <div key={a.account_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: `1px solid ${COCK.line}` }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>{a.company_code} {a.is_manual ? 'ručni unos' : (a.bank_name?.split(' ')[0] ?? '')}</div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: (a.closing ?? 0) < (tr?.alerts.low_balance_threshold_eur ?? 0) ? COCK.amber : COCK.ink }}>{fmt0(a.closing ?? 0)} €</div>
                                        <div style={{ fontSize: 9.5, color: COCK.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={accStamp(a)}>{accStamp(a)}</div>
                                    </div>
                                    {a.is_manual ? null : <Sparkline pts={histByAcc[a.account_id] ?? []} color={COCK.gold} />}
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0 6px', borderBottom: `2px solid ${COCK.line}`, fontSize: 12, fontWeight: 700 }}>
                                <span style={{ color: COCK.dim }}>Ukupno {g.code}</span>
                                <span>{fmt0(g.total)} €</span>
                            </div>
                        </div>
                    ))}
                </div>
                {/* center: projection */}
                <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: 12, zoom: z('projection') }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Projekcija tijeka novca ({projRangeCfg.label})</div>
                        <div style={{ fontSize: 11, color: COCK.dim, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ display: 'inline-flex', gap: 2 }}>
                                {PROJ_RANGES.map((r) => (
                                    <button key={r.key} type="button" onClick={() => setProjRange(r.key)} style={{ height: 20, padding: '0 7px', fontSize: 11, cursor: 'pointer', borderRadius: 4, border: `1px solid ${COCK.line}`, background: r.key === projRange ? COCK.line : 'transparent', color: r.key === projRange ? COCK.ink : COCK.dim }}>{r.short}</button>
                                ))}
                            </span>
                            <span>puna crta: stanje, isprekidana: forecast</span> {ZC('projection')}
                        </div>
                    </div>
                    <ProjectionChart payload={payload} range={projRange} />
                </div>
                {/* right: upcoming */}
                <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: 12, maxHeight: 460, overflowY: 'auto', zoom: z('upcoming') }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Nadolazeća plaćanja {ZC('upcoming')}</div>
                    {upcomingAgg.map((u, i) => {
                        const inflow = u.direction === 'inflow';
                        const label = (SRC_LABEL[u.src] ?? u.src) + (u.count > 1 ? ` · ${u.count}` : '');
                        return (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', marginBottom: 4, borderRadius: 7, background: inflow ? 'rgba(63,174,107,0.12)' : 'rgba(192,90,90,0.12)', borderLeft: `3px solid ${inflow ? COCK.green : COCK.red}` }}>
                                <span style={{ fontSize: 11, color: COCK.dim, whiteSpace: 'nowrap' }}>{fmtDay(u.date)}</span>
                                <span style={{ fontSize: 11.5, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: inflow ? COCK.green : COCK.red, whiteSpace: 'nowrap' }}>{(inflow ? '+' : '−') + fmtC(u.amount)}</span>
                            </div>
                        );
                    })}
                    {upcomingAgg.length === 0 ? <div style={{ color: COCK.dim, fontSize: 12 }}>Nema nadolazećih stavki.</div> : null}
                </div>
            </div>
            {/* bottom: waterfall + alerts */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 12 }}>
                <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: 12, zoom: z('waterfall') }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Otvoreno → Priljev → Odljev → Zatvoreno {ZC('waterfall')}</div>
                    <MiniWaterfall payload={payload} />
                    <TransferFootnote payload={payload} />
                </div>
                <div style={{ background: COCK.panel, border: `1px solid ${COCK.line}`, borderRadius: 10, padding: 12, zoom: z('alerts') }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Upozorenja {ZC('alerts')}</div>
                    {tr ? (
                        <>
                            <AlertRow tone={tr.alerts.low_balance_accounts > 0 ? COCK.amber : COCK.green} text={`${tr.alerts.low_balance_accounts} računa nisko stanje (<${fmtC(tr.alerts.low_balance_threshold_eur ?? 0)})`} />
                            <AlertRow tone={tr.alerts.overdue_count > 0 ? COCK.red : COCK.green} text={`Dospjele obveze: ${tr.alerts.overdue_count} · ${fmtC(tr.alerts.overdue_amount)}`} />
                            <AlertRow tone={tr.alerts.reserve_uncovered ? COCK.red : COCK.green} text={tr.alerts.reserve_uncovered ? 'Nepokrivena rezerva (7 dana)' : 'Rezerva pokrivena'} />
                        </>
                    ) : <div style={{ color: COCK.dim, fontSize: 12 }}>—</div>}
                </div>
            </div>
        </div>
    );
}

function AlertRow({ tone, text }: { tone: string; text: string }) {
    return <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0' }}><span style={{ width: 8, height: 8, borderRadius: 4, background: tone, flexShrink: 0 }} /><span style={{ fontSize: 12, color: COCK.ink }}>{text}</span></div>;
}

export function CashflowModule({ payload, filters, foundation, onChange, locale = 'hr', token }: { payload: CashflowPayload | null; filters: Filters; foundation: FoundationPayload; onChange: (patch: Partial<Filters>) => void; locale?: 'hr' | 'en'; token: string }) {
    const [view, setView] = useState<CashflowView>(readUrlParam('tab', 'cockpit') as CashflowView);
    useUrlSync('tab', view, view === 'cockpit');
    const hrms = isHrms();
    const viewTabs: { key: CashflowView; label: string; icon: string }[] = [
        { key: 'cockpit', label: 'Cockpit', icon: '🛰' },
        { key: 'daily', label: 'Dnevni saldo', icon: '📈' },
        { key: 'waterfall', label: 'Waterfall (po mjesecu)', icon: '🪜' },
        { key: 'forecast', label: 'Forecast (linearno)', icon: '📉' },
        { key: 'forward', label: 'Forecast (forward)', icon: '📊' },
        { key: 'schedule', label: 'Cash schedule', icon: '💰' },
        { key: 'ura', label: 'URA dospijeća', icon: '💸' },
        { key: 'ar_aging', label: 'AR Aging (IRA)', icon: '📋' },
        { key: 'correct', label: 'Ispravak stanja', icon: '🔧' },
    ];

    const companiesInOrder = useMemo(
        () => filters.companyIds.map((id) => foundation.companies.find((c) => c.id === id)).filter((c): c is Company => Boolean(c)),
        [filters.companyIds, foundation.companies],
    );

    const waterfallBars: WaterfallBar[] = useMemo(() => {
        if (!payload) return [];
        return payload.monthly.map((m) => ({
            label: m.month.slice(5),
            debit: -Math.abs(m.debit),
            credit: m.credit,
            net: m.net,
        }));
    }, [payload]);

    const runningBalancePoints = useMemo(() => {
        if (!payload) return [];
        let running = payload.balances.consolidated.opening;
        return payload.graph.map((p) => {
            running += p.net;
            return { date: p.date, net: running };
        });
    }, [payload]);

    if (!payload) {
        return <section className="card">Učitavam cashflow...</section>;
    }

    const projectionPoints = payload.projection.items.map((item) => ({
        date: item.date,
        net: item.direction === 'inflow' ? item.amount : -item.amount,
    }));

    return (
        <section className="module-stack">
            {hrms ? <Crumbs items={['Dashboard', 'Cashflow']} /> : null}
            <section className="card hero-module">
                {view !== 'cockpit' ? (
                <>
                <div className="module-heading">
                    <div>
                        <p className="eyebrow">Cashflow konsolidirano</p>
                        <h2>{formatMoney(payload.balances.consolidated.closing)}</h2>
                        <p className="muted">
                            Poznato završno stanje u odabranom rasponu. {payload.balances.note}
                        </p>
                    </div>
                    <div className="metric-pair">
                        <span>Početno <strong>{formatMoney(payload.balances.consolidated.opening)}</strong></span>
                        <span>Završno <strong>{formatMoney(payload.balances.consolidated.closing)}</strong></span>
                        <span>Promjena <strong>{formatMoney(payload.balances.consolidated.change)}</strong></span>
                        <span>Promjena s anuliranjem <strong>{formatMoney(payload.balances.consolidated.adjusted_change ?? payload.balances.consolidated.change)}</strong></span>
                    </div>
                </div>
                <section className="balance-strip">
                    <BalanceCard label="Sirovo početno" value={payload.balances.raw.opening} />
                    <BalanceCard label="Sirovo završno" value={payload.balances.raw.closing} />
                    <BalanceCard label="Sirova promjena" value={payload.balances.raw.change} />
                    <BalanceCard label="Intercompany korekcija" value={payload.balances.consolidated.intercompany_adjustment ?? 0} />
                </section>
                </>
                ) : null}

                {hrms ? (
                    <div style={{ margin: '4px 0 8px' }}>
                        <Tabs tabs={viewTabs} active={view} onChange={setView} size="sm" />
                    </div>
                ) : (
                <div className="cashflow-view-tabs" role="tablist">
                    <button type="button" role="tab" className={view === 'cockpit' ? 'active' : ''} onClick={() => setView('cockpit')}>🛰 Cockpit</button>
                    <button type="button" role="tab" className={view === 'daily' ? 'active' : ''} onClick={() => setView('daily')}>Dnevni saldo</button>
                    <button type="button" role="tab" className={view === 'waterfall' ? 'active' : ''} onClick={() => setView('waterfall')}>Waterfall (po mjesecu)</button>
                    <button type="button" role="tab" className={view === 'forecast' ? 'active' : ''} onClick={() => setView('forecast')}>Forecast (linearno)</button>
                    <button type="button" role="tab" className={view === 'forward' ? 'active' : ''} onClick={() => setView('forward')}>📊 Forecast (forward)</button>
                    <button type="button" role="tab" className={view === 'schedule' ? 'active' : ''} onClick={() => setView('schedule')}>💰 Cash schedule</button>
                    <button type="button" role="tab" className={view === 'ura' ? 'active' : ''} onClick={() => setView('ura')}>💸 URA dospijeća</button>
                    <button type="button" role="tab" className={view === 'ar_aging' ? 'active' : ''} onClick={() => setView('ar_aging')}>📋 AR Aging (IRA)</button>
                    <button type="button" role="tab" className={view === 'correct' ? 'active' : ''} onClick={() => setView('correct')}>🔧 Ispravak stanja</button>
                </div>
                )}

                {view === 'cockpit' ? (
                    <CashflowCockpit payload={payload} />
                ) : null}

                {view === 'daily' ? (
                    <>
                        <LineChart points={runningBalancePoints} />
                        <p className="muted">Kumulativno stanje računa — od početnog salda do dnevnog zatvaranja.</p>
                        {payload.graph_raw && payload.graph_raw.length > 0 ? (
                            <div className="table-wrap" style={{ marginTop: 12 }}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Stvarni priliv</th>
                                            <th>Priliv prijenosi</th>
                                            <th>Stvarni odliv</th>
                                            <th>Odliv prijenosi</th>
                                            <th>Neto (stvarno)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payload.graph.map((p) => {
                                            const raw = payload.graph_raw?.find((r) => r.date === p.date);
                                            return (
                                                <tr key={p.date}>
                                                    <td>{formatDate(p.date, locale)}</td>
                                                    <td>{formatMoney(p.credit ?? 0)}</td>
                                                    <td style={{ color: '#2f9e44' }}>{formatMoney(raw?.transfer_credit ?? 0)}</td>
                                                    <td>{formatMoney(p.debit ?? 0)}</td>
                                                    <td style={{ color: '#2f9e44' }}>{formatMoney(raw?.transfer_debit ?? 0)}</td>
                                                    <td><strong>{formatMoney(p.net)}</strong></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </>
                ) : null}

                {view === 'waterfall' ? (
                    <>
                        <WaterfallChart bars={waterfallBars} />
                        <div className="waterfall-legend">
                            <span><span className="swatch" style={{ background: '#2f9e44' }} />Priliv (potražuje)</span>
                            <span><span className="swatch" style={{ background: '#cc5c5c' }} />Odliv (duguje)</span>
                            <span><span className="swatch" style={{ background: '#c99b31' }} />Neto pozitivan</span>
                            <span><span className="swatch" style={{ background: '#6b4f1c' }} />Neto negativan</span>
                        </div>
                        <div className="table-wrap" style={{ marginTop: 12 }}>
                            <table>
                                <thead>
                                    <tr><th>Mjesec</th><th>Priliv</th><th>Odliv</th><th>Neto</th><th>Stavki</th></tr>
                                </thead>
                                <tbody>
                                    {payload.monthly.map((m) => (
                                        <tr key={m.month}>
                                            <td>{m.month}</td>
                                            <td>{formatMoney(m.credit)}</td>
                                            <td>{formatMoney(m.debit)}</td>
                                            <td><strong>{formatMoney(m.net)}</strong></td>
                                            <td>{formatNumber(m.entries)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}

                {view === 'forecast' ? (
                    <>
                        <LineChart points={payload.graph} projection={projectionPoints} />
                        {!payload.projection.available ? (
                            <p className="muted">Future projekcija je spremna, ali nema stavki dok ne importamo plan i otvorene račune.</p>
                        ) : (
                            <>
                                <div className="metric-pair" style={{ marginTop: 12 }}>
                                    <span>Projicirani priliv <strong>{formatMoney(payload.projection.summary.inflow)}</strong></span>
                                    <span>Projicirani odliv <strong>{formatMoney(payload.projection.summary.outflow)}</strong></span>
                                    <span>Projicirani neto <strong>{formatMoney(payload.projection.summary.net)}</strong></span>
                                </div>
                                {payload.projection.includes_registered_facilities ? (
                                    <p className="muted">Uključene su i procjene otplate iz modula 122–124 (leasing, krediti, kamata knjige pozajmica) nakon kraja odabranog razdoblja.</p>
                                ) : null}
                            </>
                        )}
                    </>
                ) : null}

                {view === 'forward' ? (
                    <CashflowForwardForecastTab token={token} locale={locale} />
                ) : null}

                {view === 'schedule' ? (
                    <CashflowScheduleTab token={token} locale={locale} />
                ) : null}

                {view === 'ura' ? (
                    <UraScheduleTab token={token} locale={locale} />
                ) : null}

                {view === 'ar_aging' ? (
                    <ArAgingTab token={token} locale={locale} companies={companiesInOrder} />
                ) : null}

                {view === 'correct' ? (
                    <BalanceCorrectionPanel token={token} />
                ) : null}
            </section>

            <div className="cashflow-company-stack">
                {companiesInOrder.map((company) => {
                    const summary = payload.companies.find((c) => c.code === company.code);
                    const accounts = payload.accounts.filter((account) => account.company_code === company.code);
                    return (
                        <section className="card nested-card cashflow-company-block" key={company.id}>
                            <h2 className="company-module-title">{company.code}</h2>
                            <p className="muted company-module-legal">{company.legal_name}</p>
                            {summary ? (
                                <div className="cashflow-company-metrics metric-pair">
                                    <span>Stavki u periodu <strong>{formatNumber(summary.entries)}</strong></span>
                                    <span>Neto promjena <strong>{formatMoney(summary.net)}</strong></span>
                                </div>
                            ) : (
                                <p className="muted">Nema agregata za ovu tvrtku u odabranom periodu (nema stavki izvoda).</p>
                            )}
                            <p className="company-section-eyebrow">Žiro računi</p>
                            <div className="list dense">
                                {accounts.length === 0 ? <p className="muted">Nema mapiranih računa za ovu tvrtku.</p> : null}
                                {accounts.map((account) => {
                                    const history = payload.account_history.find((item) => item.account_id === account.id);
                                    const balance = payload.balances.accounts.find((item) => item.account_id === account.id);
                                    return (
                                        <button className="list-row interactive-row" key={account.id} type="button" onClick={() => onChange({ accountId: String(account.id) })}>
                                            <div>
                                                <strong>{account.iban}</strong>
                                                <p className="muted">{account.bank_name ?? 'Banka nije mapirana'} · {account.entries} stavki</p>
                                                <div className="mini-balance-grid">
                                                    <span>Početno <strong>{formatMoney(balance?.opening ?? 0)}</strong></span>
                                                    <span>Promjena <strong>{formatMoney(balance?.change ?? account.net)}</strong></span>
                                                    <span>Završno <strong>{formatMoney(balance?.closing ?? account.net)}</strong></span>
                                                </div>
                                                <MiniChart points={(history?.points ?? []).map((point) => ({ date: point.date, net: point.running_balance }))} />
                                            </div>
                                            {(() => {
                                                const saldo = balance?.closing ?? account.net;
                                                const sign = saldo > 0 ? 'pos' : saldo < 0 ? 'neg' : 'zero';
                                                const cls = sign === 'pos' ? 'pill pill-positive' : sign === 'neg' ? 'pill pill-negative' : 'pill pill-neutral';
                                                const glyph = sign === 'pos' ? '▲ ' : sign === 'neg' ? '▼ ' : '';

                                                return <span className={cls}>{glyph}{formatMoneyAxis(saldo)}</span>;
                                            })()}
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>

            {filters.accountId ? (
                <DrilldownEntries payload={payload} filters={filters} onChange={onChange} locale={locale} />
            ) : (
                <section className="card nested-card">
                    <h3>Drilldown po računima</h3>
                    <p className="muted">Odaberi IBAN ispod za pojedinačne stavke izvoda tog računa.</p>
                </section>
            )}
        </section>
    );
}

